import React, { StrictMode } from 'react'
import { CookiesProvider } from 'react-cookie'
import { BrowserRouter } from 'react-router-dom'

import { NormalizedCacheObject } from '@apollo/client'

import {
  BATCHING_ENABLED_DEFAULTS,
  createClient
} from 'src/apollo/createClient'
import { DebugContextProvider } from 'src/shared/components/common/debug/DebugContext'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import { NavHistoryProvider } from 'src/shared/components/common/history_context/NavHistoryContext'

import AlertModalProvider from 'shared/components/common/alert_modal/AlertModal'
import { OOClientProvider } from 'shared/components/common/oo_client_provider/OOClientProvider'

import App from 'public/components/app/App'
import Routes from 'public/js/routes'

import config from 'config'
import { CookieConsentContextProvider } from 'src/public/js/cookie_consent/CookieConsentContext'
import { ExperimentContextProvider } from 'shared/components/common/ab_testing/ABTestContext'
import useTracker from 'src/lib/js/hooks/useTracker'

declare global {
  interface Window {
    __SSR_STATUS__: string | undefined | null
    __SSR_ERR_MSG__: object | undefined | null
    __OO_STATE__: NormalizedCacheObject | undefined
    __APOLLO_STATE__: NormalizedCacheObject | undefined
    __IS_BOT__: boolean | undefined
    __I18NEXT_STATE__: { language: string } | undefined
    generatedImages: { [k: string]: string }
  }
}

const ClientApp = () => {
  const flags = useFlags()
  const tracker = useTracker()
  return (
    <StrictMode>
      <CookiesProvider>
        <CookieConsentContextProvider>
          <BrowserRouter>
            <NavHistoryProvider>
              <ExperimentContextProvider
                onVariantSelected={(experimentName, variantName) => {
                  tracker.register({ [experimentName]: variantName })
                }}
              >
                <DebugContextProvider>
                  <OOClientProvider
                    client={(host) =>
                      createClient({
                        uri: host,
                        state: window.__OO_STATE__,
                        includeCredentials: true,
                        batchingOptions: BATCHING_ENABLED_DEFAULTS,
                        withRetry: true,
                        timeout: config.resources.clientQueryTimeoutMs,
                        ldFlags: flags
                      })
                    }
                  >
                    <AlertModalProvider>
                      <App>
                        <Routes />
                      </App>
                    </AlertModalProvider>
                  </OOClientProvider>
                </DebugContextProvider>
              </ExperimentContextProvider>
            </NavHistoryProvider>
          </BrowserRouter>
        </CookieConsentContextProvider>
      </CookiesProvider>
    </StrictMode>
  )
}

export default ClientApp
